import { useState, type ReactElement, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { MultilineTextSkeleton } from '@/components/ui/content-loading-skeletons';
import { cn } from '@/lib/utils';

import {
  CardCollapsedSummary,
  CardViewStateControl,
  type CardSummaryRow,
} from './_shared/cardViewState';
import { useCardBroadcast } from './_shared/cardBroadcast';
import { useCardViewController } from './_shared/useCardViewController';

export interface ClinicalSectionQuery<T> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: readonly T[] | undefined;
  readonly refetch: () => unknown;
}

export interface ClinicalSectionLabels {
  readonly title: string;
  /** Optional inline element next to the title (e.g. a count chip). */
  readonly titleSuffix?: ReactNode;
  readonly empty: string;
  readonly emptyIcon?: ReactNode;
  readonly loading: string;
  readonly errorHeading: string;
  readonly errorMessage: string;
  readonly codeLabel: string;
  readonly retryLabel: string;
}

interface ClinicalSectionProps<T> {
  readonly id: string;
  readonly labels: ClinicalSectionLabels;
  readonly query: ClinicalSectionQuery<T>;
  readonly itemKey: (item: T, index: number) => string;
  readonly renderItem: (item: T, index: number) => ReactNode;
  readonly headerAction?: ReactNode;
  readonly toolbar?: ReactNode;
  /**
   * Optional override for the data body. When provided, the section
   * still renders the loading and error states from `query`, but
   * delegates the populated body to this node — used when consumers
   * need to group, paginate, or otherwise depart from the default flat
   * `<ul>` (Phenotypes section's body-system grouping, etc).
   */
  readonly customBody?: ReactNode;
  /** Whether the section starts expanded (default true). */
  readonly defaultOpen?: boolean;
  /**
   * Field/value rows for the collapsed read-only summary. When provided, the header shows the 4-state
   * view control and the collapsed section renders this compact list (PTV2-1157).
   */
  readonly summary?: readonly CardSummaryRow[];
}

export function ClinicalSection<T>({
  id,
  labels,
  query,
  itemKey,
  renderItem,
  headerAction,
  toolbar,
  customBody,
  defaultOpen = true,
  summary,
}: ClinicalSectionProps<T>): ReactElement {
  const titleId = `${id}-title`;
  const [open, setOpen] = useState(defaultOpen);
  const { summaryView, viewState, applyViewState } = useCardViewController(open, setOpen);
  const showSummary = !open && summary !== undefined && summaryView !== 'off';
  // Read-only / view-only mode (pedigree Lock): the broadcast is `forced` — the section can't be
  // expanded, so the header is non-interactive and shows no chevron (PTV2-1157). No-op elsewhere.
  const locked = useCardBroadcast()?.forced === true;

  const headerInner = (
    <>
      {!locked && (
        <ChevronDown
          aria-hidden="true"
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      )}
      <CardTitle
        id={titleId}
        className={cn(
          'flex min-w-0 flex-1 items-baseline gap-2 truncate text-[13.5px] font-semibold tracking-tight transition-colors',
          !locked && 'group-hover:text-primary',
        )}
      >
        <span className="truncate">{labels.title}</span>
        {labels.titleSuffix !== undefined && labels.titleSuffix}
      </CardTitle>
    </>
  );

  return (
    <Card id={id} aria-labelledby={titleId} className="scroll-mt-4 rounded-xl">
      <CardHeader className={cn('px-5 py-3.5 space-y-0', (open || showSummary) && 'border-b border-border')}>
        {/* flex-wrap so the eye/action controls drop to a second row (header grows) instead of being
            clipped when the panel is very narrow (e.g. the 260px pedigree side panel). */}
        <div data-card-header-row className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {locked ? (
            <div className="flex min-w-0 flex-[1_1_10rem] items-center gap-2 text-left">
              {headerInner}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                setOpen((v) => !v);
              }}
              aria-expanded={open}
              className="flex min-w-0 flex-[1_1_10rem] items-center gap-2 text-left group"
            >
              {headerInner}
            </button>
          )}
          {(summary !== undefined || (open && headerAction !== undefined)) && (
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {summary !== undefined && (
                <CardViewStateControl variant="hover" value={viewState} onChange={applyViewState} className="shrink-0" />
              )}
              {open && headerAction !== undefined && <div className="shrink-0">{headerAction}</div>}
            </div>
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="p-5">
          {toolbar !== undefined && <div className="mb-4">{toolbar}</div>}
          <ClinicalSectionBody
            labels={labels}
            query={query}
            itemKey={itemKey}
            renderItem={renderItem}
            customBody={customBody}
          />
        </CardContent>
      )}
      {showSummary && (
        <CardContent className="px-5 py-2.5">
          <CardCollapsedSummary rows={summary} summaryView={summaryView} />
        </CardContent>
      )}
    </Card>
  );
}

function ClinicalSectionBody<T>({
  labels,
  query,
  itemKey,
  renderItem,
  customBody,
}: Omit<ClinicalSectionProps<T>, 'id' | 'headerAction' | 'toolbar'>): ReactElement {
  if (query.isPending) {
    return <MultilineTextSkeleton ariaLabel={labels.loading} />;
  }

  if (query.isError) {
    return (
      <FetchErrorCallout
        className="max-w-none"
        error={query.error}
        heading={labels.errorHeading}
        message={labels.errorMessage}
        codeLabel={labels.codeLabel}
        headingAs="p"
        actions={
          <Button
            size="sm"
            onClick={() => {
              Promise.resolve(query.refetch()).catch(/* noop */() => { /* intentional */ });
            }}
          >
            {labels.retryLabel}
          </Button>
        }
      />
    );
  }

  const items = query.data ?? [];
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        {labels.emptyIcon !== undefined && (
          <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
            {labels.emptyIcon}
          </div>
        )}
        <p className="text-sm text-muted-foreground">{labels.empty}</p>
      </div>
    );
  }

  if (customBody !== undefined) {
    return <>{customBody}</>;
  }

  return (
    <ul className="space-y-0 divide-y divide-border/50">
      {items.map((item, index) => (
        <li key={itemKey(item, index)} className="py-3 first:pt-0 last:pb-0">
          {renderItem(item, index)}
        </li>
      ))}
    </ul>
  );
}
